import "./security.css";
import { DownloadSimpleIcon, KeyIcon } from "@phosphor-icons/react";
import { useRef, useState, type Ref } from "react";
import type { AccountSecurityManagement } from "../types";
import { authenticationActionError } from "./presentation";

export type AccountDataExportState =
  | "confirming"
  | "failed"
  | "freshness-required"
  | "idle"
  | "pending"
  | "ready";

export function AccountDataExportControl({
  accountSecurity,
}: {
  accountSecurity: AccountSecurityManagement;
}) {
  const actionRef = useRef<HTMLButtonElement>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [state, setState] = useState<AccountDataExportState>("idle");

  function cancel(): void {
    setState("idle");
    queueMicrotask(() => actionRef.current?.focus());
  }

  async function download(): Promise<void> {
    setState("pending");
    setErrorMessage("");
    try {
      await accountSecurity.exportAccountData();
      setState("ready");
    } catch (cause) {
      const message = authenticationActionError(
        cause,
        "Your Account data could not be exported.",
      );
      if (/sign in again/i.test(message)) {
        setErrorMessage("Sign in again to download your Account data.");
        setState("freshness-required");
      } else {
        setErrorMessage(message);
        setState("failed");
      }
    }
  }

  async function recoverFreshness(): Promise<void> {
    setState("pending");
    setErrorMessage("");
    try {
      await accountSecurity.requestFreshSignIn();
      setState("idle");
    } catch (cause) {
      setErrorMessage(
        authenticationActionError(
          cause,
          "Sign-in could not be opened. Please try again.",
        ),
      );
      setState("freshness-required");
    }
  }

  return (
    <AccountDataExportCard
      actionRef={actionRef}
      errorMessage={errorMessage}
      onCancel={cancel}
      onConfirm={() => void download()}
      onFreshSignIn={() => void recoverFreshness()}
      onRequest={() => setState("confirming")}
      onRetry={() => void download()}
      state={state}
    />
  );
}

export function AccountDataExportCard({
  actionRef,
  errorMessage = "",
  onCancel = () => undefined,
  onConfirm = () => undefined,
  onFreshSignIn = () => undefined,
  onRequest = () => undefined,
  onRetry = () => undefined,
  state,
}: {
  actionRef?: Ref<HTMLButtonElement>;
  errorMessage?: string;
  onCancel?: () => void;
  onConfirm?: () => void;
  onFreshSignIn?: () => void;
  onRequest?: () => void;
  onRetry?: () => void;
  state: AccountDataExportState;
}) {
  const actionLabel =
    state === "freshness-required"
      ? "Sign in again"
      : state === "failed"
        ? "Try again"
        : state === "ready"
          ? "Download again"
          : "Download my data";
  return (
    <section className="pm-passport-card pm-account-data-export-card">
      <header>
        <div>
          <h2>Account data export</h2>
          <p>Keep a private, readable copy of your Puzzleini journey.</p>
        </div>
        <small>Private JSON</small>
      </header>

      {state === "confirming" ? (
        <div
          aria-label="Confirm Account data export"
          aria-modal="true"
          className="pm-security-confirmation pm-data-export-confirmation"
          onKeyDown={(event) => {
            if (event.key === "Escape") onCancel();
          }}
          role="dialog"
        >
          <div>
            <strong>Download your Account data?</strong>
            <p>
              The JSON file includes your profile, Friendships, owned puzzles,
              progression, and accessible Game History. It excludes passwords,
              sessions, provider tokens, Room Conversation, and other
              players&apos; private Account data.
            </p>
          </div>
          <button autoFocus onClick={onCancel} type="button">
            Cancel
          </button>
          <button onClick={onConfirm} type="button">
            Download JSON
          </button>
        </div>
      ) : state === "pending" ? (
        <div className="pm-security-state" role="status">
          <strong>Preparing your private export…</strong>
          <span>Keep this page open while Puzzleini gathers your data.</span>
          <button disabled type="button">
            Preparing download…
          </button>
        </div>
      ) : (
        <div className="pm-data-export-action">
          <span className="pm-auth-method-icon">
            <DownloadSimpleIcon aria-hidden="true" size={21} weight="duotone" />
          </span>
          <div>
            <strong>
              {state === "ready"
                ? "Your export is ready"
                : "Download a private copy"}
            </strong>
            <p>Versioned JSON, prepared only after a fresh Account sign-in.</p>
          </div>
          <button
            aria-label={actionLabel}
            onClick={
              state === "freshness-required"
                ? onFreshSignIn
                : state === "failed"
                  ? onRetry
                  : onRequest
            }
            ref={actionRef}
            type="button"
          >
            {state === "freshness-required" ? (
              <KeyIcon aria-hidden="true" size={17} />
            ) : (
              <DownloadSimpleIcon aria-hidden="true" size={17} />
            )}
            {actionLabel}
          </button>
        </div>
      )}

      {state === "ready" ? (
        <p className="pm-security-feedback success" role="status">
          Your private JSON export was downloaded.
        </p>
      ) : state === "failed" || state === "freshness-required" ? (
        <p className="pm-security-feedback error" role="alert">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}
